import { MAX_SEND_ATTACHMENTS } from '@/convex/products/ai/lib/send_attachment_limit_constants';
import { normalizeLocale, type SupportedLocale } from './config';

const N = MAX_SEND_ATTACHMENTS;

/**
 * Localized refusal for a message with more than MAX_SEND_ATTACHMENTS files (#7626). The server
 * sends `PENDING_UPLOAD_TOO_MANY_FILES: <English sentence>`
 * (`convex/products/ai/lib/send_attachment_limit.helpers.ts`); the shared
 * error localizer maps the code to this copy.
 */
const COPY: Record<SupportedLocale, string> = {
  en: `Attach at most ${N} files per message. Send the rest in another message.`,
  ar: `أرفق ${N} ملفات كحد أقصى في كل رسالة. أرسل الباقي في رسالة أخرى.`,
  es: `Adjunta como máximo ${N} archivos por mensaje. Envía el resto en otro mensaje.`,
  'pt-BR': `Anexe no máximo ${N} arquivos por mensagem. Envie o restante em outra mensagem.`,
  fr: `Joignez au maximum ${N} fichiers par message. Envoyez le reste dans un autre message.`,
  it: `Allega al massimo ${N} file per messaggio. Invia il resto in un altro messaggio.`,
  ko: `메시지당 최대 ${N}개의 파일을 첨부할 수 있어요. 나머지는 다른 메시지로 보내 주세요.`,
  de: `Hänge höchstens ${N} Dateien pro Nachricht an. Sende den Rest in einer weiteren Nachricht.`,
  ja: `1件のメッセージに添付できるファイルは${N}件までです。残りは別のメッセージで送信してください。`,
  hi: `हर संदेश में ज़्यादा से ज़्यादा ${N} फ़ाइलें जोड़ें। बाकी फ़ाइलें दूसरे संदेश में भेजें।`,
  ru: `Прикрепите не более ${N} файлов к одному сообщению. Остальные отправьте в другом сообщении.`,
  zh: `每条消息最多附加 ${N} 个文件。请在另一条消息中发送其余文件。`,
  'zh-TW': `每則訊息最多附加 ${N} 個檔案。請在另一則訊息中傳送其餘檔案。`,
  vi: `Đính kèm tối đa ${N} tệp mỗi tin nhắn. Hãy gửi phần còn lại trong một tin nhắn khác.`,
  id: `Lampirkan maksimal ${N} file per pesan. Kirim sisanya di pesan lain.`,
};

const CODE_PATTERN = /\bPENDING_UPLOAD_TOO_MANY_FILES\b/;

/** The localized refusal sentence, for the composer's own pre-send check. */
export function getSendAttachmentLimitCopy(
  locale: string | null | undefined
): string {
  return COPY[normalizeLocale(locale)];
}

/**
 * Localized refusal when `message` carries the too-many-files code, else
 * null. The code may sit inside a wrapped transport message
 * (`[CONVEX M(...)] ... ConvexError: PENDING_UPLOAD_TOO_MANY_FILES: ...`).
 */
export function getSendAttachmentLimitMessage(
  message: string,
  locale: string | null | undefined
): string | null {
  if (!CODE_PATTERN.test(message)) return null;
  return getSendAttachmentLimitCopy(locale);
}
